import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { DatePickerField } from '@/components/datePickerField';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { DateStatus, PeriodNotice } from '../../recordMeta';

type DateBandProps = {
    /** ISO `YYYY-MM-DD`. */
    date: string;
    onDateChange: (date: string) => void;
    onToday: () => void;
    onYesterday: () => void;
    /** The counts, or `null` while the picker's read is in flight. */
    status: DateStatus | null;
    /** Set when the date is before every recipe, or a server refusal named it. */
    dateError: string | null;
    periodNotice: PeriodNotice | null;
    isDisabled: boolean;
};

/**
 * Step 1 — the date, and what it makes available.
 *
 * **It leads because it decides.** The recordable list is resolved by date on
 * the server, so a date chosen after an operation retracts the choice with no
 * explanation. It used to sit inside the generated form, below the picker; now
 * it is above, and it appears exactly once on the screen.
 *
 * **The status line is derived text, not decoration.** It is the only place a
 * user learns the date is narrowing their options — "60 configured · 58
 * available on this date" — so it recomputes with the date and never with the
 * render.
 *
 * **The period notice is negative-only.** An open period says nothing: a tenant
 * that has generated no fiscal year has not opted into period control at all,
 * and a cheerful "August 2026 is open" would be describing a calendar that may
 * not exist. A closed one is worth interrupting for, because it is a refusal the
 * user would otherwise meet only after filling the whole form.
 */
export const DateBand: React.FC<DateBandProps> = ({
    date,
    onDateChange,
    onToday,
    onYesterday,
    status,
    dateError,
    periodNotice,
    isDisabled,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('operations.record.date.step')}
            className="flex flex-col gap-3 rounded-2xl border border-separator bg-surface p-4"
            {...getNodeTestIdProp('operations-record-date-band')}
        >
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="w-full sm:w-56">
                    <DatePickerField
                        error={dateError ?? undefined}
                        id="operations-record-date"
                        isDisabled={isDisabled}
                        label={t('operations.record.date.step')}
                        name="operations-record-date"
                        onChange={onDateChange}
                        value={date}
                    />
                </div>

                <div className="flex gap-2">
                    <Button
                        isDisabled={isDisabled}
                        onPress={onToday}
                        size="sm"
                        variant="outline"
                        {...getNodeTestIdProp('operations-record-date-today')}
                    >
                        {t('operations.record.date.today')}
                    </Button>
                    <Button
                        isDisabled={isDisabled}
                        onPress={onYesterday}
                        size="sm"
                        variant="outline"
                        {...getNodeTestIdProp(
                            'operations-record-date-yesterday',
                        )}
                    >
                        {t('operations.record.date.yesterday')}
                    </Button>
                </div>
            </div>

            <p
                className="text-xs text-muted"
                {...getNodeTestIdProp('operations-record-date-explainer')}
            >
                {t('operations.record.date.explainer')}
            </p>

            <DateStatusLine status={status} />

            {periodNotice ? (
                <p
                    className="flex items-start gap-2 text-xs text-warning"
                    {...getNodeTestIdProp('operations-record-date-period')}
                >
                    <Icon
                        aria-hidden
                        className="mt-0.5 shrink-0"
                        icon="gravity-ui:triangle-exclamation"
                    />
                    {periodNotice === 'closed'
                        ? t('operations.record.date.periodClosed')
                        : t('operations.record.date.periodNotDefined')}
                </p>
            ) : null}
        </section>
    );
};

/**
 * The counts, in one of three sentences.
 *
 * A `null` status is "not known yet", never "none": the line keeps its height
 * with a placeholder rather than claiming zero operations while the read is
 * still in flight.
 */
const DateStatusLine: React.FC<{ status: DateStatus | null }> = ({
    status,
}) => {
    const { t } = useTranslation('pages');

    if (status === null) {
        return (
            <div
                className="h-4 w-56 animate-pulse rounded bg-surface-secondary"
                {...getNodeTestIdProp('operations-record-date-status-skeleton')}
            />
        );
    }

    if (status.kind === 'none') {
        return null;
    }

    return (
        <p
            className="flex items-start gap-2 text-xs text-foreground"
            {...getNodeTestIdProp('operations-record-date-status')}
        >
            <Icon
                aria-hidden
                className="mt-0.5 shrink-0 text-success"
                icon="gravity-ui:circle-check"
            />
            {status.kind === 'all'
                ? t('operations.record.date.allAvailable', {
                      count: status.available,
                  })
                : t('operations.record.date.narrowed', {
                      configured: status.configured,
                      available: status.available,
                  })}
        </p>
    );
};
